import { useFeatureFlag } from "@/shared/hooks/useFeatureFlag";

/**
 * Per-station WorkOS feature flag that gates the Station Admin dashboard,
 * mirroring co-review (`co-approval-workflow`), HITL (`hitl`), and Incident
 * Management (`incident-management`). It is defined and toggled in the online
 * WorkOS configuration and surfaced per station in KardaAdmin → Station
 * Settings; enabling it for a station turns the feature on for that station's
 * users at their next login (flags arrive with the auth context — no
 * redeploy).
 *
 * Unlike Incident Management there is no build-time API precondition: the
 * dashboard aggregates APIs every build already has configured (reports,
 * evidence, and the agent API for the activity briefing).
 */
export const ADMIN_DASHBOARD_FEATURE_FLAG = "admin-dashboard";

/**
 * Pure flag check — no auth context, for tests and non-hook callers.
 */
export function isAdminDashboardFlagOn(
  featureFlags?: string[] | null,
): boolean {
  return (featureFlags ?? []).includes(ADMIN_DASHBOARD_FEATURE_FLAG);
}

/**
 * Whether the Station Admin dashboard should be shown to the current user.
 * The per-station WorkOS flag is the only gate here; the station_admin ROLE
 * check stays separate (RequireRole on the route, role check in the sidebar).
 */
export function useAdminDashboardEnabled(): boolean {
  return useFeatureFlag(ADMIN_DASHBOARD_FEATURE_FLAG);
}

/**
 * Per-station WorkOS flag that gates the Internal Affairs TAB independently of
 * `admin-dashboard`. The surrounding Station Admin area is reachable when
 * EITHER flag is on (RequireStationAdminArea), so reaching Internal Affairs
 * requires the station_admin role + this flag — NOT `admin-dashboard`. A
 * station can run the dashboard without IA, IA without the dashboard, or both.
 *
 * Like `admin-dashboard` it is a pure WorkOS flag with no build-time API
 * precondition — Internal Affairs rides the agent API the dashboard already
 * uses — and is toggled per station in KardaAdmin → Station Settings.
 */
export const INTERNAL_AFFAIRS_FEATURE_FLAG = "internal-affairs";

/**
 * Pure flag check — no auth context, for tests and non-hook callers.
 */
export function isInternalAffairsFlagOn(
  featureFlags?: string[] | null,
): boolean {
  return (featureFlags ?? []).includes(INTERNAL_AFFAIRS_FEATURE_FLAG);
}

/**
 * Whether the Internal Affairs tab should be shown. Composes WITH — not
 * instead of — useAdminDashboardEnabled (see the flag doc above).
 */
export function useInternalAffairsEnabled(): boolean {
  return useFeatureFlag(INTERNAL_AFFAIRS_FEATURE_FLAG);
}
